<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'settings.printers',
    path: 'printers',
    component: 'Printers',
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Printers',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const table = ref(null);

  const $refs = {
    get table() {
      return table.value;
    },
  };

  const open = (...args) => {
    const [oid] = args;

    return $imodal.value
      .show({
        name: 'PrinterConfig',
        props: { oid },
      })
      .then((res) => loadData('items'));
  };

  const objectType = ref('printer');
  const items = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_3343864469950044_editable = computed(() => false);
  const b_3343864469950044_columns = computed(() => [
    {
      name: 'id',
      header: $fn.value.pgettext('Printer', 'ID'),
      width: '180px',
      formatter: {
        name: 'template',
        value:
          '<div>\n\
  <a class="text-nowrap"\n\
     href="#"\n\
     @click.prevent="globs.open(row.id)"\n\
  >\n\
    {{ value }}\n\
  </a>\n\
</div>\n\
',
      },
    },
    {
      name: 'name',
      header: $fn.value.pgettext('Printer', 'Name'),
      width: '180px',
    },
    {
      name: 'uri',
      header: $fn.value.pgettext('Printer', 'URI'),
      width: '180px',
    },
  ]);

  const $data = computed(() => ({
    objectType: objectType.value,
    items: items.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_3343864469950044_editable: b_3343864469950044_editable.value,
    b_3343864469950044_columns: b_3343864469950044_columns.value,
  }));

  const $$load_items = () => {
    return $fn.value
      .fetch('list_object_by_type', {
        parameters: { object_type: $data.value.objectType },
      })
      .then((items) => _.orderBy(items, ['created_at'], ['asc']));
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'objectType' },
      { name: 'items' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_3343864469950044_editable', nodeId: '3343864469950044' },
      { name: 'b_3343864469950044_columns', nodeId: '3343864469950044' },
    ],
    loaders: { items: $$load_items },
    ux: {},
    dataRefs: {
      objectType,
      items,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_3343864469950044_editable,
      b_3343864469950044_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_f0fdw09e4_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_6705964765214521_script_n59wii6sw_value_callable = async (
    $event
  ) => {
    return open();
  };
  const act_6705964765214521_script_n59wii6sw_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_6705964765214521_script_n59wii6sw_value_callable($event);
      },
    };
  };
  const act_8116269939469002_showModal_go7p1jlpw_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'FileSelector',
      props: {
        url: $fn.value.urlFor('import_object_by_type', {
          object_type: objectType.value,
        }),
        accept: '.json,.yaml,.yml',
      },
    };
  };
  const act_3343864469950044_script_pwnzf3kk0_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value
        .crud('object_by_type')
        .delete({ ...$event.data.row, object_type: objectType.value })
    );
  };
  const act_3343864469950044_script_pwnzf3kk0_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3343864469950044_script_pwnzf3kk0_value_callable($event);
      },
    };
  };

  const act_7315092382398562_closeModal_f0fdw09e4 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_f0fdw09e4_getActionArgs,
    });
  const inst_evh_7315092382398562_close_vz5qyqce6 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_f0fdw09e4,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_vz5qyqce6],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_e9aim809r =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_kh0yr2igr =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_e9aim809r,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_kh0yr2igr],
    event: 'reload',
  });
  const act_8320016629450276_reloadComponentData_ngvly5b6v =
    new actionTypes.ReloadComponentDataAction({
      displayName: 'reload',
    });
  const inst_evh_8320016629450276_clickPreventStop_3ah9yodi3 =
    new actionTypes.EventHandlerImpl({
      action: act_8320016629450276_reloadComponentData_ngvly5b6v,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_8320016629450276_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8320016629450276_clickPreventStop_3ah9yodi3],
      event: 'click.prevent.stop',
    });
  const act_6705964765214521_script_n59wii6sw = new actionTypes.ScriptAction({
    actionArgs: act_6705964765214521_script_n59wii6sw_getActionArgs,
  });
  const inst_evh_6705964765214521_clickPreventStop_gt4iomacp =
    new actionTypes.EventHandlerImpl({
      action: act_6705964765214521_script_n59wii6sw,
      event: 'click.prevent.stop',
      displayName: 'script',
    });
  const inst_evh_6705964765214521_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6705964765214521_clickPreventStop_gt4iomacp],
      event: 'click.prevent.stop',
    });
  const act_8116269939469002_showModal_go7p1jlpw =
    new actionTypes.ShowModalAction({
      actionArgs: act_8116269939469002_showModal_go7p1jlpw_getActionArgs,
    });
  const inst_evh_8116269939469002_clickPreventStop_nadus7het =
    new actionTypes.EventHandlerImpl({
      action: act_8116269939469002_showModal_go7p1jlpw,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_8116269939469002_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8116269939469002_clickPreventStop_nadus7het],
      event: 'click.prevent.stop',
    });
  const act_3343864469950044_script_pwnzf3kk0 = new actionTypes.ScriptAction({
    actionArgs: act_3343864469950044_script_pwnzf3kk0_getActionArgs,
  });
  const inst_evh_3343864469950044_delete_czwcgakqh =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_script_pwnzf3kk0,
      event: 'delete',
      displayName: 'script',
    });
  const inst_evh_3343864469950044_delete = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3343864469950044_delete_czwcgakqh],
    event: 'delete',
  });

  const actions = {
    inst_evh_7315092382398562_close_vz5qyqce6,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_kh0yr2igr,
    inst_evh_7315092382398562_reload,
    inst_evh_8320016629450276_clickPreventStop_3ah9yodi3,
    reload: inst_evh_8320016629450276_clickPreventStop_3ah9yodi3,
    inst_evh_8320016629450276_clickPreventStop,
    inst_evh_6705964765214521_clickPreventStop_gt4iomacp,
    inst_evh_6705964765214521_clickPreventStop,
    inst_evh_8116269939469002_clickPreventStop_nadus7het,
    inst_evh_8116269939469002_clickPreventStop,
    inst_evh_3343864469950044_delete_czwcgakqh,
    inst_evh_3343864469950044_delete,
  };

  const evh_7315092382398562_close_vz5qyqce6 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_vz5qyqce6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_kh0yr2igr = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_kh0yr2igr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop_3ah9yodi3 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop_3ah9yodi3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6705964765214521_clickPreventStop_gt4iomacp = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6705964765214521_clickPreventStop_gt4iomacp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6705964765214521_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_6705964765214521_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8116269939469002_clickPreventStop_nadus7het = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8116269939469002_clickPreventStop_nadus7het.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8116269939469002_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8116269939469002_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_delete_czwcgakqh = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_delete_czwcgakqh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_delete = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_delete.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Printers"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="overflow-hidden d-flex flex-column h-100 p-3" v-if="loaded">
      <ItpBox alignItems="center" :gap="2" class="mb-3">
        <strong>
          {{ pgettext('Printers', 'Printer configurations') }}
        </strong>
        <ItpButton
          icon="fa-sync-alt"
          :text="gettext('Reload')"
          variant="light"
          size="sm"
          :spinning="actions.reload.isRunning"
          @click.prevent.stop="
            evh_8320016629450276_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('Printers', 'Add printer...')"
          variant="light"
          size="sm"
          @click.prevent.stop="
            evh_6705964765214521_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          icon="fa-file-invoice"
          :text="pgettext('Printers', 'Export')"
          variant="light"
          size="sm"
          :disabled="!items || !items.length"
          use-auth
          force-download
          downloadFileName="printer-configurations.json"
          :href="
            $fn.urlFor('export_object_by_type', { object_type: objectType })
          "
        >
        </ItpButton>
        <ItpButton
          icon="fa-file-invoice"
          :text="pgettext('Printers', 'Import')"
          variant="light"
          size="sm"
          @click.prevent.stop="
            evh_8116269939469002_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </ItpBox>
      <ItpDataTable
        tableId="Settings.Printers"
        :editable="b_3343864469950044_editable"
        :value="items"
        :globs="{ open }"
        :columns="b_3343864469950044_columns"
        class="itp-datatable--flex-fill"
        ref="table"
        v-if="loaded"
        @delete="evh_3343864469950044_delete($event, {})"
      >
      </ItpDataTable>
    </div>
  </ItpPage>
</template>
